import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Alert,
  Divider,
  Button,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ButtonOne from "./normal-button";
import ButtonTwo from "./dashboard-button";

const formatCurrency = (amount = 0, currency = "INR") =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 2,
  }).format(amount);

const formatRatePerMessage = (data) => {
  const { rate_per_message, rate_per_message_min, rate_per_message_max, currency } = data || {};

  if (
    rate_per_message_min != null &&
    rate_per_message_max != null &&
    Number(rate_per_message_min) !== Number(rate_per_message_max)
  ) {
    return `${formatCurrency(rate_per_message_min, currency)} - ${formatCurrency(rate_per_message_max, currency)}`;
  }

  return formatCurrency(rate_per_message, currency);
};

const WalletErrorDialog = ({ open, data, onClose, onRecharge }) => {
  if (!data) return null;

  // Other blocks (e.g. unpaid invoices) come back with just a message, so only show what the response carries
  const hasBalanceData = data.current_balance != null && data.required_balance != null;
  const hasBroadcastDetails =
    data.recipients != null ||
    data.rate_per_message != null ||
    data.rate_per_message_min != null ||
    data.template_category != null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ErrorOutlineIcon sx={{ color: "#d32f2f", fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#d32f2f" }}>
            Insufficient Wallet Balance
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="error" sx={{ mb: 3 }}>
          {data?.message}
        </Alert>

        {/* Balance Overview */}
        {hasBalanceData && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Balance Overview
          </Typography>

          <Box sx={{ backgroundColor: "#fef5e7", border: "1px solid #f39c12", p: 2 }}>
            <Row label="Current Balance" value={formatCurrency(data?.current_balance, data?.currency)} />
            <Row label="Required Balance" value={formatCurrency(data?.required_balance, data?.currency)} />
            <Divider sx={{ my: 1 }} />
            <Row
              label="Shortfall"
              value={formatCurrency(data?.required_balance - data?.current_balance, data?.currency)}
              valueColor="#d32f2f"
              bold
            />
          </Box>
        </Box>
        )}

        {/* Broadcast Details */}
        {hasBroadcastDetails && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Broadcast Details
          </Typography>

          <Box sx={{ backgroundColor: "#f5f5f5", p: 2 }}>
            <Row label="Recipients" value={data.recipients || 0} />
            <Row
              label="Rate per Message"
              value={formatRatePerMessage(data)}
            />
            <Row label="Template Category" value={data.template_category || "N/A"} />
          </Box>
        </Box>
        )}

        {hasBalanceData && (
          <Alert severity="info" icon={<AccountBalanceWalletIcon />}>
            Please recharge your wallet to continue.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <ButtonTwo onClick={onClose}>{hasBalanceData ? "Cancel" : "Close"}</ButtonTwo>
        {hasBalanceData && (
          <ButtonOne variant="contained" onClick={onRecharge}>
            Recharge Wallet
          </ButtonOne>
        )}
      </DialogActions>
    </Dialog>
  );
};

const Row = ({ label, value, valueColor, bold }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
    <Typography variant="body2">{label}:</Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: bold ? 700 : 400, color: valueColor }}
    >
      {value}
    </Typography>
  </Box>
);

export default WalletErrorDialog;
